import React, { useEffect, useRef, useState, useCallback } from "react";
import Control from "./Control";
import * as MS from "../../../styles/MediaStyled";
import * as QS from "../../../styles/QuizStyled";
import * as CS from "../../../styles/CourseViewStyled";
import englishLang from "../../../utils/Languages/english.json";
import {
  convertSecondsToTimeString,
  convertToSeconds,
  formatTime,
  replaceBaseUrl,
  timeStringToSeconds,
} from "../../../utils/Helpers";
import ReactHlsPlayer from "react-hls-player";
import Hls from "hls.js";
import { get } from "lodash";
import axios from "../../../utils/axios";
import { Course, Episode } from "../../../utils/api";
import { Formik, Field, Form, FieldArray } from "formik";
import darkCheckRadio from "../../../assets/images/darkCheckRadio.png";
import checkRadio from "../../../assets/images/checkRadio.png";
import baseRadio from "../../../assets/images/baseRadio.png";
import darkBaseRadio from "../../../assets/images/darkBaseRadio.png";
import { Modal } from "../../../components/PopUp/ModalTwo";
import * as SEC from "../../../styles/SectionStyledTwo";
import successModal from "../../../assets/images/successModal.png";
import clockTimer from "../../../assets/images/clockTimer.png";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import playIcon from "../../../assets/images/play.png";
import {
  setEpisodeId,
  setEpisodeRefresh,
  setErrorPop,
  setErrorPopMsg,
  setQuizEpisode,
  setTimeStamp,
  setVideoPlay,
  setVolumeValue,
} from "../../../store/action";
import ModalComplete from "../../../components/Modal/ModalComplete";
import { debounce } from "lodash";
import greenCheck from "../../../assets/images/greenCheck.png";
import circleCross from "../../../assets/images/circleCross.png";
import CircleLoader from "../../../components/Loader/CircleLoader";
import ReactAudioPlayer from "react-audio-player";
import landt from "../../../assets/images/landt.png";
import rainLogoDarkTwo from "../../../assets/images/rainLogoDarkTwo.png";

// Network Monitor Class for Bandwidth Control
class NetworkMonitor {
  constructor(hls) {
    this.hls = hls;
    this.networkSpeed = 0;
    this.isSlowConnection = false;
    this.lastSpeedCheck = Date.now();
    this.speedSamples = [];
    this.adaptiveSettings = {
      slow: {
        maxBufferLength: 15,
        abrBandWidthFactor: 0.6,
        maxQualityLevel: 1,
      },
      medium: {
        maxBufferLength: 25,
        abrBandWidthFactor: 0.75,
        maxQualityLevel: 2,
      },
      fast: {
        maxBufferLength: 35,
        abrBandWidthFactor: 0.85,
        maxQualityLevel: -1,
      },
    };

    this.setupNetworkMonitoring();
  }

  setupNetworkMonitoring() {
    // Monitor connection type if available
    if ("connection" in navigator) {
      const connection = navigator.connection;
      this.updateNetworkSettings(connection);

      connection.addEventListener("change", () => {
        this.updateNetworkSettings(connection);
      });
    }

    // Monitor HLS events for network performance
    this.hls.on(Hls.Events.FRAG_LOAD_PROGRESS, (event, data) => {
      this.calculateNetworkSpeed(data);
    });

    this.hls.on(Hls.Events.ERROR, (event, data) => {
      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        this.handleNetworkError(data);
      }
    });

    this.hls.on(Hls.Events.BUFFER_EMPTY, () => {
      this.handleBufferEmpty();
    });

    this.hls.on(Hls.Events.BUFFER_STALLED, () => {
      this.handleBufferStalled();
    });
  }

  updateNetworkSettings(connection) {
    const effectiveType = connection.effectiveType;
    const downlink = connection.downlink; // Mbps
    let settings;

    // Determine settings based on connection type and downlink speed
    if (effectiveType === "slow-2g" || effectiveType === "2g" || downlink < 1) {
      settings = this.adaptiveSettings.slow;
      this.isSlowConnection = true;
    } else if (effectiveType === "3g" || downlink < 5) {
      settings = this.adaptiveSettings.medium;
      this.isSlowConnection = false;
    } else {
      settings = this.adaptiveSettings.fast;
      this.isSlowConnection = false;
    }

    // Apply settings to HLS
    Object.assign(this.hls.config, {
      maxBufferLength: settings.maxBufferLength,
      abrBandWidthFactor: settings.abrBandWidthFactor,
    });

    // Set quality limits
    if (
      settings.maxQualityLevel >= 0 &&
      this.hls.levels.length > settings.maxQualityLevel
    ) {
      this.hls.loadLevel = Math.min(
        this.hls.loadLevel,
        settings.maxQualityLevel
      );
    }
  }

  calculateNetworkSpeed(data) {
    if (data.loaded && data.total && data.stats && data.stats.loading) {
      const duration = data.stats.loading.end - data.stats.loading.start;
      if (duration > 0) {
        const bitsLoaded = data.loaded * 8;
        const speed = bitsLoaded / (duration / 1000); // bits per second

        this.speedSamples.push(speed);
        if (this.speedSamples.length > 8) {
          // Keep fewer samples for faster response
          this.speedSamples.shift();
        }

        // Calculate weighted average (recent samples weigh more)
        const weights = this.speedSamples.map(
          (_, i) => (i + 1) / this.speedSamples.length
        );
        const weightedSum = this.speedSamples.reduce(
          (sum, speed, i) => sum + speed * weights[i],
          0
        );
        const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
        const avgSpeed = weightedSum / totalWeight;

        this.networkSpeed = avgSpeed;

        // Immediate upgrade check for very fast fragment loads
        this.checkImmediateUpgrade(speed, avgSpeed);

        this.adaptBasedOnSpeed(avgSpeed);
      }
    }
  }

  checkImmediateUpgrade(currentFragmentSpeed, averageSpeed) {
    const speedKbps = currentFragmentSpeed / 1000;
    const avgSpeedKbps = averageSpeed / 1000;
    const currentLevel = this.hls.currentLevel;

    // If a single fragment loads much faster than required, consider immediate upgrade
    if (speedKbps > avgSpeedKbps * 2 && currentLevel >= 0) {
      const currentLevelRequirement =
        this.qualityLevels[currentLevel]?.minSpeed || 0;

      // If fragment speed is 3x higher than current level requirement
      if (speedKbps > currentLevelRequirement * 3) {
        const targetLevel = this.getBestQualityForSpeed(speedKbps * 0.7); // Conservative estimate

        if (targetLevel > currentLevel) {
          // Only upgrade if we haven't tried recently
          const now = Date.now();
          if (now - this.lastUpgradeCheck > 1000) {
            // 1 second cooldown
            this.hls.nextLoadLevel = Math.min(currentLevel + 1, targetLevel);
            this.lastUpgradeCheck = now;
            console.log(
              `Immediate upgrade triggered by fast fragment load: ${speedKbps.toFixed(
                0
              )} kbps`
            );
          }
        }
      }
    }
  }

  adaptBasedOnSpeed(speed) {
    const speedKbps = speed / 1000;
    const now = Date.now();

    // Only adapt every 5 seconds to prevent frequent switches
    if (now - this.lastSpeedCheck < 5000) return;
    this.lastSpeedCheck = now;

    if (speedKbps < 300) {
      // Very slow connection - force lowest quality
      this.hls.nextLoadLevel = 0;
      this.hls.config.maxBufferLength = 10;
    } else if (speedKbps < 800) {
      // Slow connection - limit to lower qualities
      this.hls.nextLoadLevel = Math.min(1, this.hls.levels.length - 1);
      this.hls.config.maxBufferLength = 15;
    } else if (speedKbps > 3000) {
      // Fast connection - allow auto quality
      this.hls.nextLoadLevel = -1;
      this.hls.config.maxBufferLength = 30;
    }
  }

  handleNetworkError(data) {
    console.warn("Network error detected:", data);

    // Implement progressive fallback
    switch (data.details) {
      case Hls.ErrorDetails.FRAG_LOAD_TIMEOUT:
        // Reduce buffer size and quality
        this.hls.config.maxBufferLength = Math.max(
          8,
          this.hls.config.maxBufferLength - 3
        );
        if (this.hls.levels.length > 1) {
          this.hls.nextLoadLevel = Math.max(0, this.hls.currentLevel - 1);
        }
        break;

      case Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT:
        // Reload manifest with delay
        setTimeout(() => {
          this.hls.startLoad();
        }, 2000);
        break;

      case Hls.ErrorDetails.BUFFER_STALLED_ERROR:
        this.handleBufferStalled();
        break;
    }
  }

  handleBufferEmpty() {
    // Reduce quality when buffer empties
    if (this.hls.currentLevel > 0) {
      this.hls.nextLoadLevel = Math.max(0, this.hls.currentLevel - 1);
    }
  }

  handleBufferStalled() {
    // Aggressive quality reduction for stalled buffer
    this.hls.nextLoadLevel = 0;
    this.hls.config.maxBufferLength = Math.max(
      5,
      this.hls.config.maxBufferLength - 5
    );
  }

  getNetworkStatus() {
    return {
      speed: Math.round(this.networkSpeed / 1000), // kbps
      isSlowConnection: this.isSlowConnection,
      currentLevel: this.hls.currentLevel,
      bufferLength: this.hls.config.maxBufferLength,
    };
  }
}

// Enhanced HLS configuration function
const createOptimizedHls = () => {
  return new Hls({
    // Adaptive bitrate settings
    abrEwmaDefaultEstimate: 500000, // Conservative initial estimate (500kbps)
    abrEwmaSlowVoD: 0.95, // Slower adaptation for VoD content
    abrEwmaFastVoD: 0.95,
    abrEwmaSlowLive: 0.95,
    abrEwmaFastLive: 0.95,
    abrMaxWithRealBitrate: false,
    abrBandWidthFactor: 0.8, // Use 80% of estimated bandwidth
    abrBandWidthUpFactor: 0.7, // Conservative upward switching

    // Buffer management for smooth playback
    maxBufferLength: 25, // Balanced buffer for stability
    maxMaxBufferLength: 50,
    maxBufferSize: 40 * 1000 * 1000, // 40MB buffer
    maxBufferHole: 0.3,

    // Loading and stall recovery
    maxLoadingDelay: 4,
    maxRetryAttempt: 4,
    maxRetryDelay: 32,
    retryDelayBackOff: 2,

    // Fragment loading optimization
    fragLoadingTimeOut: 15000,
    manifestLoadingTimeOut: 8000,

    // Stall detection and recovery
    lowBufferWatchdogPeriod: 0.5,
    highBufferWatchdogPeriod: 3,
    nudgeOffset: 0.1,
    nudgeMaxRetry: 3,

    // Performance optimizations
    enableWorker: true,
    enableSoftwareAES: true,
    startLevel: -1, // Auto-select initial quality
    capLevelToPlayerSize: true, // Don't load higher quality than player size

    // Progressive loading
    progressive: true,

    // Error recovery
    enableStreamingFailures: true,
  });
};

const MediaPlayer = ({
  theme,
  handleStartLearning,
  handleIsChanging,
  isChanging,
  handleCourseMenu,
}) => {
  const episodeId = useSelector((state) => state?.episodeId);
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const courseId = useSelector((state) => state?.courseId);
  const timeStamp = useSelector((state) => state?.timeStamp);
  const episodeRefresh = useSelector((state) => state?.episodeRefresh);
  const volumeValue = useSelector((state) => state?.volumeValue);
  const selectLang = useSelector((state) => state?.selectLang);
  const selectLangJson = useSelector((state) => state?.selectLangJson);
  const episodeSelectLang = useSelector((state) => state?.episodeSelectLang);
  const bannerThumb = useSelector((state) => state?.bannerThumb);
  const hlsRef = useRef(null);
  const controlRef = useRef(null);
  const logoAddPlayRef = useRef(null);
  const [qualities, setQualities] = useState([]);
  const [currentQuality, setCurrentQuality] = useState("auto");
  const [quesOne, setQuesOne] = useState(false);
  const [quesTwo, setQuesTwo] = useState(false);
  const [quesThree, setQuesThree] = useState(false);
  const videoPlayerRef = useRef(null);
  const logoPlayRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(volumeValue === 0 ? true : false);
  const [volume, setVolume] = useState(volumeValue);
  const [seek, setSeek] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isDuration, setIsDuration] = useState(0);
  const [isSamDuration, setIsSamDuration] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [totalSecodo, setTotalSeconds] = useState(0);
  const mouseMoveTimeout = useRef(null);
  const [quizData, setQuizData] = useState(null);
  const [filterQuizData, setFilterQuizData] = useState([]);
  const [courseEpisodeData, setCourseEpisodeData] = useState([]);
  const languageId = useSelector((state) => state?.languageId);
  const [isPopPo, setIsPopPo] = useState(false);
  const [stopTimeStamp, setStopTimeStamp] = useState(null);
  const [hlsInstance, setHlsInstance] = useState(null);
  const [showsucess, setShowsucess] = useState(true);
  const [successMsg, setSuccessMsg] = useState(selectLangJson.episode_complete);
  const [apiCalled, setApiCalled] = useState(false);
  const [quizId, setQuizId] = useState("");
  const [nextEpisode, setNextEpisode] = useState("");
  const [quizShown, setQuizShown] = useState(false);
  const [isLogoPlay, setIsLogoPlay] = useState(false);
  const [isLogoAddDuration, setIsLogoAddDuration] = useState(null);
  const debouncedNameFunction = useRef();
  const episodeIdRef = useRef(episodeId);
  const [answered, setAnswered] = useState(
    new Array(filterQuizData?.question?.length)?.fill(false)
  );
  const profileData = useSelector((state) => state?.profileData);
  const [videoSrc, setVideoSrc] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [currentURL, setCurrentURL] = useState("");
  const [isLoader, setIsLoader] = useState(false);
  const [isLoaderEpisode, setIsLoaderEpisode] = useState(false);
  const [isLoaderAdd, setIsLoaderAdd] = useState(false);
  const [isAddMuted, setIsAddMuted] = useState(true);
  const [isAddArr, setIsAddArr] = useState([]);
  const [isCourseComplete, setIsCourseComplete] = useState({});
  const [isLogoAddPlay, setIsLogoAddPlay] = useState(false);
  const [addLogoUrl, setAddLogoUrl] = useState("");
  const [isMusicShow, setIsMusicShow] = useState(false);
  const [maxWatchedTime, setMaxWatchedTime] = useState(0);
  const [isForward, setIsForward] = useState(false);
  const [isBackward, setIsBackward] = useState(false);
  const [isLogoPaused, setIsLogoPaused] = useState(false);

  // New state for network monitoring
  const [networkMonitor, setNetworkMonitor] = useState(null);
  const [isHlsLoading, setIsHlsLoading] = useState(false);
  const [networkStatus, setNetworkStatus] = useState({
    speed: 0,
    isSlowConnection: false,
    currentLevel: -1,
    bufferLength: 25,
  });

  // Simple loader functions
  const showLoader = () => {
    setIsHlsLoading(true);
  };

  const hideLoader = () => {
    setIsHlsLoading(false);
  };

  const dynamicRoutes = useSelector((state) =>
    state?.dynamicRoutes ? `/${state?.dynamicRoutes}` : ""
  );

  useEffect(() => {
    episodeIdRef.current = episodeId;
  }, [episodeId]);

  const handleMetadata = (e) => {
    setIsLoaderEpisode(false);
    setIsDuration(formatTime(e.target.duration));
    setIsSamDuration(e.target.duration);
    setIsLogoAddDuration(Math.floor(e.target.duration));
    const savedVolume = parseFloat(localStorage.getItem("rainLearnerVolume"));
    setVolume(savedVolume);
    videoPlayerRef.current.volume = savedVolume;
    // Don't hide loader here - video might still be loading fragments
    console.log('Video: METADATA_LOADED - keeping loader visible until canplaythrough');
  };

  const handleAddMetadata = (e) => {
    console.log("Slide/add video metadata loaded");
    setIsLoaderAdd(false);
    // Play background music if present
    if (audioRef.current && audioRef.current.audioEl && audioRef.current.audioEl.current) {
      audioRef.current.audioEl.current.play().catch((error) => {
        console.log("Error playing background music:", error);
      });
    }
  };

  const startLearning = async () => {
    if (document.fullscreenElement) {
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      } catch (error) {
        console.error("Error exiting fullscreen:", error);
      }
    }
    let url = Episode.episodeComplete;
    setIsPlaying(false);
    try {
      const { data } = await axios.post(url, {
        courseId: courseId,
        episodeId: episodeId,
        languageId: episodeSelectLang,
      });
      if (data?.data) {
        setIsCourseComplete(data?.data);
        setQuizId(data?.data?.quiz?._id);
      }
      if (data?.data?.nextEpisodeId) {
        setNextEpisode(data?.data?.nextEpisodeId);
      }
      setIsPlaying(false);
      setIsPopPo(true);
      setQuizData({});
      setTimeout(() => {
        if (data?.data?.COURSE_COMPLETED) {
          handleStartLearning();
          handleCourseMenu();
          if (data?.data?.VIEW_POLICY_DOCUMENTS) {
            navigate(`${dynamicRoutes}/learner/courses/policy-document`);
          }
        }
        dispatch(setEpisodeRefresh(episodeRefresh + 1));
      }, 2000);
    } catch (error) {
      console.log(error.response.data.message);
      dispatch(setVideoPlay(false));
      if (error.response && error.response.status === 400) {
        dispatch(setErrorPop(true));
        dispatch(setErrorPopMsg(error?.response?.data?.message));
      }
    }
  };

  const [isNameFunctionCalled, setIsNameFunctionCalled] = useState(false);

  const nameFuntion = async (e) => {
    if (isNameFunctionCalled) return;
    setIsNameFunctionCalled(true);
    let url = Episode.timeSubmit;
    const formValues = {
      courseId: courseId,
      episodeId: episodeId,
      languageId: episodeSelectLang,
      last_time_stamp: convertSecondsToTimeString(e),
    };

    try {
      const { data } = await axios.post(url, formValues);
      if (videoPlayerRef.current) {
        const currentTime = videoPlayerRef.current.currentTime || 0;
        const duration = videoPlayerRef.current.duration || 0;
        if (
          duration > 0 &&
          !apiCalled &&
          Math.abs(duration - currentTime) < 0.5
        ) {
          startLearning();
        }
      }
    } catch (error) {
      dispatch(setVideoPlay(false));
      if (error.response && error.response.status === 400) {
        dispatch(setErrorPop(true));
        dispatch(setErrorPopMsg(error?.response?.data?.message));
      }
    } finally {
      setIsNameFunctionCalled(false);
      setApiCalled(false);
    }
  };

  const handlePlay = () => {
    var playPromise = videoPlayerRef?.current?.play();

    if (playPromise !== undefined) {
      playPromise
        .then((_) => {
          setIsPlaying(true);
          setIsLoaderAdd(false);
          console.log('Video: PLAY_PROMISE_RESOLVED - clearing all loaders');

          // Immediately clear all loaders on successful play
          hideLoader();
          setIsLoaderEpisode(false);
          setIsLoader(false);

          // Fallback: Clear loaders after 1 second if video events don't trigger
          setTimeout(() => {
            if (videoPlayerRef.current && !videoPlayerRef.current.paused) {
              console.log('Video: FALLBACK_TIMEOUT - ensuring loaders are cleared');
              hideLoader();
              setIsLoaderEpisode(false);
              setIsLoader(false);
            }
          }, 1000);
        })
        .catch((error) => {
          console.log('Video play error:', error);
          // Clear loaders even on error
          hideLoader();
          setIsLoaderEpisode(false);
          setIsLoader(false);
        });
    }
  };

  const handlePause = () => {
    videoPlayerRef?.current?.pause();
    setIsPlaying(false);
    setControlsVisible(true);
    clearTimeout(mouseMoveTimeout.current);

    // Clear loader on manual pause - user intentionally paused
    console.log('Video: MANUAL_PAUSE - clearing loader');
    hideLoader();
    setIsLoaderEpisode(false);
    setIsLoader(false);

    // Use actual video time instead of state variable (state can be stale)
    const actualCurrentTime = videoPlayerRef?.current?.currentTime || 0;
    if (actualCurrentTime > 2) {
      nameFuntion(actualCurrentTime);
      getCourseEpisodeData();
    }
  };

  const toggleMute = () => {
    const newMuteStatus = !isMuted;
    if (isMuted) {
      localStorage.setItem("rainLearnerVolume", 1);
    } else {
      localStorage.setItem("rainLearnerVolume", 0);
    }
    setIsMuted(newMuteStatus);
    videoPlayerRef.current.muted = newMuteStatus;
  };

  const volumeChangeHandler = (event) => {
    const newVolume = parseFloat(event.target.value) / 100;
    setVolume(newVolume);
    dispatch(setVolumeValue(newVolume));
    localStorage.setItem("rainLearnerVolume", newVolume);
    videoPlayerRef.current.volume = newVolume;
  };

  const handleTimeUpdate = () => {
    if (videoPlayerRef.current) {
      const currentTime = videoPlayerRef.current.currentTime || 0;
      const duration = videoPlayerRef.current.duration;
      const seekValue = (currentTime / duration) * 100;

      setCurrentTime(currentTime);
      setSeek(seekValue);

      setMaxWatchedTime((prevMax) => Math.max(prevMax, currentTime));

      if (duration > 0 && !apiCalled && duration === currentTime) {
        setApiCalled((apiCalled) => {
          if (!apiCalled) {
            debouncedNameFunction.current(currentTime);
            return true;
          }
          return apiCalled;
        });
      }

      // Update network status periodically
      if (networkMonitor) {
        const status = networkMonitor.getNetworkStatus();
        setNetworkStatus(status);
      }
    }
  };

  useEffect(() => {
    debouncedNameFunction.current = debounce(
      (currentTime) => {
        nameFuntion(currentTime, episodeId);
        getCourseEpisodeData();
        setIsAddArr([]);
      },
      1000,
      {
        leading: true,
        trailing: false,
      }
    );

    return () => {
      debouncedNameFunction.current.cancel();
    };
  }, [episodeId, timeStamp]);

  // For Admin
  const handleSeekAdmin = (event) => {
    const newSeek = parseInt(event.target.value, 10);
    setSeek(newSeek);
    const seekTime = (newSeek / 100) * videoPlayerRef.current.duration;
    videoPlayerRef.current.currentTime = seekTime;
    setIsDuration(formatTime(videoPlayerRef.current.duration));
    nameFuntion(videoPlayerRef?.current?.currentTime);
    getCourseEpisodeData();
  };

  // For Learner
  const handleSeek = (event) => {
    const newSeekValue = parseInt(event.target.value, 10);
    const newSeekTime = (newSeekValue / 100) * videoPlayerRef.current.duration;

    if (newSeekTime <= maxWatchedTime) {
      videoPlayerRef.current.currentTime = newSeekTime;
      setSeek(newSeekValue);
    } else {
      const maxSeekValue =
        (maxWatchedTime / videoPlayerRef.current.duration) * 100;
      setSeek(maxSeekValue);
      videoPlayerRef.current.currentTime = maxWatchedTime;
    }

    setIsDuration(formatTime(videoPlayerRef.current.duration));
    nameFuntion(videoPlayerRef?.current?.currentTime);
    getCourseEpisodeData();
  };

  // For Forward
  const handleSeekForward = (event) => {
    const newSeekValue = parseInt(event.target.value, 10);
    const video = videoPlayerRef.current;
    if (!video || !video.duration) return;

    const duration = video.duration;
    const desiredTime = (newSeekValue / 100) * duration;

    if (desiredTime < maxWatchedTime) {
      const cappedSeekValue = (maxWatchedTime / duration) * 100;
      video.currentTime = maxWatchedTime;
      setSeek(cappedSeekValue);
    } else {
      video.currentTime = desiredTime;
      setSeek(newSeekValue);
    }

    setIsDuration(formatTime(duration));
    nameFuntion(video.currentTime);
    getCourseEpisodeData();
  };

  const updateTime = () => {
    if (videoPlayerRef.current) {
      const currentTime = videoPlayerRef.current.currentTime;

      if (currentTime > maxWatchedTime) {
        setMaxWatchedTime(currentTime);
      }

      const currentSeekValue =
        (currentTime / videoPlayerRef.current.duration) * 100;
      setSeek(currentSeekValue);
    }
  };

  useEffect(() => {
    const player = videoPlayerRef.current;

    player?.addEventListener("timeupdate", updateTime);

    return () => {
      player?.removeEventListener("timeupdate", updateTime);
    };
  }, [maxWatchedTime]);

  const getResolutionFromBitrate = (bitrate) => {
    if (bitrate <= 200000) return "144p";
    if (bitrate <= 500000) return "360p";
    if (bitrate <= 2000000) return "720p";
    if (bitrate <= 5000000) return "1080p";
    return "4K";
  };

  // Enhanced HLS setup with network monitoring
  useEffect(() => {
    if (Hls.isSupported() && videoSrc) {
      const video = videoPlayerRef.current;
      const hls = createOptimizedHls();
      hlsRef.current = hls;

      // Initialize network monitor
      const monitor = new NetworkMonitor(hls);
      setNetworkMonitor(monitor);

      hls.attachMedia(video);

      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(videoSrc);
      });

      setHlsInstance(hls);

      // Simple HLS loader events - more persistent approach
      hls.on(Hls.Events.MANIFEST_LOADING, () => {
        console.log('HLS: MANIFEST_LOADING - showLoader');
        showLoader();
      });

      // Don't hide loader after manifest parsed - keep it visible for fragments
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('HLS: MANIFEST_PARSED - keeping loader visible');
        // Keep loader visible, don't hide yet
      });

      // hls.on(Hls.Events.LEVEL_SWITCHING, () => {
      //   console.log('HLS: LEVEL_SWITCHING - showLoader');
      //   showLoader();
      // });

      // hls.on(Hls.Events.LEVEL_LOADING, () => {
      //   console.log('HLS: LEVEL_LOADING - showLoader');
      //   showLoader();
      // });

      // // Don't hide loader after level loaded - fragments might still be loading
      // hls.on(Hls.Events.LEVEL_LOADED, () => {
      //   console.log('HLS: LEVEL_LOADED - keeping loader visible');
      //   // Keep loader visible, fragments might still be loading
      // });

      // hls.on(Hls.Events.FRAG_LOADING, () => {
      //   console.log('HLS: FRAG_LOADING - showLoader');
      //   showLoader();
      // });

      // Only hide loader when video is actually ready to play
      hls.on(Hls.Events.BUFFER_STALLED_ERROR, () => showLoader());
      hls.on(Hls.Events.BUFFER_EMPTY, () => showLoader());

      // Enhanced buffering state management
      // hls.on(Hls.Events.BUFFER_APPENDING, () => {
      //   setIsBuffering(false);
      // });

      // hls.on(Hls.Events.BUFFER_EOS, () => {
      //   setIsBuffering(false);
      // });

      // hls.on(Hls.Events.WAITING, () => {
      //   setIsBuffering(true);
      // });

      // hls.on(Hls.Events.BUFFER_EMPTY, () => {
      //   setIsBuffering(true);
      // });

      // hls.on(Hls.Events.BUFFER_STALLED, () => {
      //   setIsBuffering(true);
      // });

      hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        const levels = data.levels.map((level, index) => ({
          index,
          resolution: level.height
            ? `${level.height}p`
            : `${getResolutionFromBitrate(level?.bitrate)}`,
          bitrate: level.bitrate,
        }));
        setCurrentQuality(hls.currentLevel);
        setQualities([
          { index: -1, resolution: "Auto", bitrate: 0 },
          ...levels,
        ]);
      });

      // Monitor level switches for bandwidth reporting
      // hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
      //   const level = hls.levels[data.level];
      //   setCurrentQuality(data.level);
      //   if (monitor) {
      //     const status = monitor.getNetworkStatus();
      //     setNetworkStatus(status);
      //   }
      // });

      // Enhanced error handling
      hls.on(Hls.Events.ERROR, (event, data) => {
        console.warn("HLS Error:", data);

        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.log("Fatal network error encountered, trying to recover");
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log("Fatal media error encountered, trying to recover");
              hls.recoverMediaError();
              break;
            default:
              console.log("Fatal error, cannot recover");
              hls.destroy();
              break;
          }
        }
      });

      return () => {
        hls.destroy();
        setNetworkMonitor(null);
      };
    } else if (
      videoPlayerRef.current?.canPlayType("application/vnd.apple.mpegurl")
    ) {
      videoPlayerRef.current.src = videoSrc;
    }
  }, [videoSrc]);

  const handleQualityChange = (levelIndex) => {
    if (hlsInstance) {
      const currentTime = videoPlayerRef.current.currentTime || 0;
      const isPaused = videoPlayerRef.current.paused;
      hlsInstance.currentLevel = levelIndex;
      hlsInstance.startLoad();
      hlsInstance.once(Hls.Events.FRAG_LOADED, () => {
        videoPlayerRef.current.currentTime = currentTime;
        if (!isPaused) {
          videoPlayerRef?.current?.play();
        }
      });
      setCurrentQuality(levelIndex);
    }
  };

  const checkQuiz = useCallback(() => {
    const fixedCurrentTime = Math.floor(currentTime);
    let filterQuiz = quizData?.question?.filter(
      (val) => val.totalSeconds === fixedCurrentTime
    );

    

    if (filterQuiz?.length > 0 && !quizShown) {
      setFilterQuizData({ question: filterQuiz });
      videoPlayerRef?.current?.pause();
      setIsPlaying(false);
      nameFuntion(videoPlayerRef?.current?.currentTime);
      getCourseEpisodeData();
      setQuesOne(true);
      setIsLoaderEpisode(false);
      setQuizShown(true);
    } else if (!filterQuiz?.length && quizShown) {
      setQuizShown(false);
    }
  }, [currentTime, quizData, quizShown]);

  useEffect(() => {
    const debouncedCheckQuiz = debounce(checkQuiz, 100);
    debouncedCheckQuiz();
    return () => {
      debouncedCheckQuiz?.cancel();
    };
  }, [currentTime, quizData, quizShown, checkQuiz]);

  const toggleFullScreen = () => {
    const videoElement = controlRef.current.closest(".media-class-box");

    if (!document.fullscreenElement) {
      if (videoElement.requestFullscreen) {
        videoElement.requestFullscreen();
      } else if (videoElement.webkitRequestFullscreen) {
        videoElement.webkitRequestFullscreen();
      } else if (videoElement.msRequestFullscreen) {
        videoElement.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  const handleMouseOver = () => {
    if (!controlsVisible) {
      mouseMoveTimeout.current = setTimeout(() => {
        setControlsVisible(false);
      }, 3000);
    }
  };

  const handleMouseMove = () => {
    setControlsVisible(true);
    clearTimeout(mouseMoveTimeout.current);
    mouseMoveTimeout.current = setTimeout(() => {
      setControlsVisible(false);
    }, 3000);
  };

  const handleMouseLeave = () => {
    clearTimeout(mouseMoveTimeout.current);
    setControlsVisible(false);
  };

  useEffect(() => {
    return () => {
      clearTimeout(mouseMoveTimeout.current);
    };
  }, []);

  const videoTimeConvert = (e, time, endTime) => {
    if (e === 1) {
      return 0; // Show immediately at start (before episode)
    } else if (e === 3) {
      return endTime - 1; // Show near end
    } else {
      return convertToSeconds(time); // Show at custom time
    }
  };

  const getCourseEpisodeData = async () => {
    let url = Episode.userEpisodeWatchInfo;
    try {
      const { data } = await axios.post(url, {
        courseId: courseId,
        episodeId: episodeId,
        languageId: episodeSelectLang,
      });
      if (data?.data) {
        setCourseEpisodeData(data?.data);
        setIsForward(data?.data?.video_forworld || false);
        setIsBackward(data?.data?.video_backworld || false);
        const timeStr = get(data?.data, "last_time_stamp", "00:00:00");
        const parts = timeStr?.split(":");

        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        const seconds = parseInt(parts[2], 10);
        setNextEpisode("");
        const totalSeconds = hours * 3600 + minutes * 60 + seconds;
        if (totalSeconds) {
          setTotalSeconds(totalSeconds);
          setCurrentTime(totalSeconds);
        }
        setStopTimeStamp(get(data?.data, "stop_time_stamp", null));
        if (data?.data?.url) {
          setVideoSrc(get(data?.data, "url", ""));
          setLogoUrl(get(data?.data, "logoUrl", ""));
          setIsLogoPlay(get(data?.data, "logoPlay", false));
          if (get(data?.data, "logoPlay")) {
            setIsLoader(true);
          }
          dispatch(setVideoPlay(true));
        } else {
          dispatch(setVideoPlay(false));
        }
        setApiCalled(false);
        dispatch(setQuizEpisode(false));
        const upcomingQuizQuestions = get(
          data?.data,
          "upcoming_quiz[0].questions",
          []
        );
        setAnswered(new Array(upcomingQuizQuestions?.length)?.fill(false));
        if (upcomingQuizQuestions.length > 0) {
          setQuizData({
            question: upcomingQuizQuestions,
          });
        }
        // Merge slide_with_music and video arrays and apply placement types to all
        if (data?.data?.video?.length || data?.data?.slide_with_music?.length) {
          let mergeAddData = [
            ...(data?.data?.video || []),
            ...(data?.data?.slide_with_music || []),
          ];
          if (mergeAddData?.length > 0) {
            const timeStr = get(data?.data, "end_time_stamp", "00:00:00");
            const parts = timeStr?.split(":");

            const hours = parseInt(parts[0], 10);
            const minutes = parseInt(parts[1], 10);
            const seconds = parseInt(parts[2], 10);
            const totalSeconds = hours * 3600 + minutes * 60 + seconds;
            setIsAddArr(
              mergeAddData?.map((val, ind) => {
                return {
                  id: ind + 1,
                  src: val?.convert_url,
                  totalTime: videoTimeConvert(
                    val.placementType,
                    val.time,
                    totalSeconds
                  ),
                  musicId: val.musicId ? val.musicId : null,
                  musicUrl: val.url ? val.url : null,
                };
              })
            );
          }
        }
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        if (error.response.data?.data?.HOME_SCREEN) {
          dispatch(setVideoPlay(false));
        }
      }
      if (error.response && error.response.status === 400) {
        dispatch(setErrorPop(true));
        dispatch(setErrorPopMsg(error?.response?.data?.message));
      }
      dispatch(setVideoPlay(false));
    }
  };

  useEffect(() => {
    if (courseId && episodeId) {
      getCourseEpisodeData();
    }
  }, [courseId, episodeId]);

  const handleQuizSubmit = async (values) => {
    setIsLoading(true);
    let url = Episode.interactiveQuizSubmit;
    let formValues = {
      courseId: courseId,
      episodeId: episodeId,
      languageId: episodeSelectLang,
      QuizId: get(values, "question[0]._id", ""),
      answerOptionsId: values?.question[0]?.answerOptions?.find(
        (val) => val.isAnswers
      )?._id,
    };

    try {
      const { data } = await axios.post(url, formValues);
      nameFuntion((videoPlayerRef.current.currentTime += 1));
      getCourseEpisodeData();
      setQuesOne(false);
      setIsLoading(false);
      setIsPlaying(true);
      setFilterQuizData([]);
      getCourseEpisodeData();
      handlePlay();
    } catch (error) {
      console.log(error.response.data.message);
      setIsLoading(false);
      if (error.response && error.response.status === 400) {
        dispatch(setErrorPop(true));
        dispatch(setErrorPopMsg(error?.response?.data?.message));
      }
    }
  };

  useEffect(() => {
    const player = videoPlayerRef.current;
    if (player) {
      player.currentTime = totalSecodo;
    }
  }, [totalSecodo]);

  useEffect(() => {
    const player = videoPlayerRef.current;
    if (player) {
      const handleLoadedMetadata = (e) => {
        handleMetadata(e);
      };

      // Simple video event listeners for HLS loader - more conservative hiding
      const handleVideoPlaying = () => {
        console.log('Video: PLAYING - hideLoader + clearing all loader states');
        hideLoader();
        setIsLoaderEpisode(false);
        setIsLoader(false);
      };
      const handleVideoWaiting = () => {
        console.log('Video: WAITING - showLoader');
        showLoader();
      };
      const handleVideoCanPlayThrough = () => {
        console.log('Video: CANPLAYTHROUGH - hideLoader + clearing all loader states');
        hideLoader();
        setIsLoaderEpisode(false);
        setIsLoader(false);
      };
      const handleVideoStalled = () => {
        console.log('Video: STALLED - showLoader');
        showLoader();
      };
      const handleVideoPause = () => {
        console.log('Video: PAUSE - clearing all loader states');
        // Always clear loaders when video pauses (user initiated or automatic)
        hideLoader();
        setIsLoaderEpisode(false);
        setIsLoader(false);
      };
      const handleVideoLoadStart = () => {
        console.log('Video: LOADSTART - showLoader');
        showLoader();
      };

      player.addEventListener("loadedmetadata", handleLoadedMetadata);

      // Add new simple loader event listeners
      player.addEventListener("playing", handleVideoPlaying);
      player.addEventListener("waiting", handleVideoWaiting);
      player.addEventListener("canplaythrough", handleVideoCanPlayThrough);
      player.addEventListener("stalled", handleVideoStalled);
      player.addEventListener("pause", handleVideoPause);
      player.addEventListener("loadstart", handleVideoLoadStart);

      return () => {
        player.removeEventListener("loadedmetadata", handleLoadedMetadata);

        // Remove new simple loader event listeners
        player.removeEventListener("playing", handleVideoPlaying);
        player.removeEventListener("waiting", handleVideoWaiting);
        player.removeEventListener("canplaythrough", handleVideoCanPlayThrough);
        player.removeEventListener("stalled", handleVideoStalled);
        player.removeEventListener("pause", handleVideoPause);
        player.removeEventListener("loadstart", handleVideoLoadStart);
      };
    }
  }, []);

  const handleClose = () => {
    setIsPopPo(false);
  };

  useEffect(() => {
    if (videoSrc) {
      // Show loader immediately when video source changes
      showLoader();
      handlePlay();
    }
  }, [videoSrc]);

  // Reset logo paused state when logo play starts/ends
  useEffect(() => {
    if (isLogoPlay) {
      setIsLogoPaused(false);
    } else {
      setIsLogoPaused(false);
    }
  }, [isLogoPlay]);

  const restartEpisode = async () => {
    let url = Episode.restartEpisode;
    try {
      const { data } = await axios.post(url, {
        courseId: courseId,
        episodeId: episodeId,
        languageId: episodeSelectLang,
      });
      dispatch(setQuizEpisode(true));
      if (videoPlayerRef.current) {
        videoPlayerRef.current.currentTime = 0;
        handlePlay();
      }
      setIsPopPo(false);
    } catch (error) {
      console.log(error.response.data.message);
      if (error.response && error.response.status === 400) {
        dispatch(setErrorPop(true));
        dispatch(setErrorPopMsg(error?.response?.data?.message));
      }
    }
  };

  const getCorrectAnswer = (e) => {
    let correctAnwser = e?.find(
      (val) => val.isAnswers === true && val.isAnswer === true
    );
    if (correctAnwser) {
      return (
        <QS.DesciptionContainer themeColor={theme}>
          <QS.CorrectRow themeColor={theme}>
            <img src={greenCheck} alt="" />
            <p>{selectLangJson.correct_answer}</p>
          </QS.CorrectRow>
          {correctAnwser?.description ? (
            <QS.DesciptionBox themeColor={theme}>
              <p className="title">{selectLangJson.description} :</p>
              <p className="desc">{correctAnwser?.description}</p>
            </QS.DesciptionBox>
          ) : (
            ""
          )}
        </QS.DesciptionContainer>
      );
    } else {
      let wrongAnswer = e?.find(
        (val) => val.isAnswers === false && val.isAnswer === true
      );
      if (wrongAnswer) {
        return (
          <QS.DesciptionContainer themeColor={theme}>
            <QS.CorrectRow wrongAns={true}>
              <img src={circleCross} alt="" />
              <p>{selectLangJson.incorrect_answer}</p>
            </QS.CorrectRow>
            {wrongAnswer?.description ? (
              <QS.DesciptionBox themeColor={theme}>
                <p className="title">{selectLangJson.description} :</p>
                <p className="desc">{wrongAnswer?.description}</p>
              </QS.DesciptionBox>
            ) : (
              ""
            )}
          </QS.DesciptionContainer>
        );
      }
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden || !document.hasFocus()) {
        // If logo is playing, pause the logo video
        if (isLogoPlay && logoPlayRef.current && !logoPlayRef.current.paused) {
          logoPlayRef.current.pause();
          setIsPlaying(false);
          setIsLogoPaused(true);
        } else {
          // For main video, use existing pause logic
          handlePause();
          setIsPlaying(false);
        }
      }
    };

    const handleFocus = () => {
      if (!document.hidden && document.hasFocus()) {
        // Automatic resume disabled - user must manually resume using center play button
        // if (isLogoPlay && logoPlayRef.current && logoPlayRef.current.paused) {
        //   logoPlayRef.current.play().then(() => {
        //     setIsPlaying(true);
        //     setIsLogoPaused(false);
        //   }).catch((error) => {
        //     console.log("Error resuming logo video:", error);
        //   });
        // }
        // Don't pause main video on focus - that doesn't make sense
      }
    };

    window.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [isLogoPlay]);

  useEffect(() => {
    if (quesOne) {
      handlePause();
    }
  }, [quesOne]);

  const handleVideoEnd = () => {
    console.log("handleVideoEnd called", { isLogoPlay });
    if (isLogoPlay) {
      // Logo video ended, proceed to main video
      console.log("Logo ended, proceeding to main video");
      setIsLogoPlay(false);

      // Clear ALL loaders immediately
      setIsLoader(false);
      setIsLoaderEpisode(false);
      hideLoader();

      // Main video should already be loaded, just play it
      setIsPlaying(true);
      if (videoPlayerRef.current) {
        console.log("Starting main video playback after logo");
        // Ensure video is ready before playing
        if (videoPlayerRef.current.readyState >= 2) {
          handlePlay();
        } else {
          // If not ready, wait for canplay event
          const playWhenReady = () => {
            handlePlay();
            videoPlayerRef.current.removeEventListener('canplay', playWhenReady);
          };
          videoPlayerRef.current.addEventListener('canplay', playWhenReady);
        }
      }
    } else {
      setIsLogoPlay(false);
      setIsPlaying(true);
      setIsLoaderEpisode(false);
      hideLoader();
      console.log("End of playlist");
    }
  };

  const handleVideoReady = () => {
    console.log("Logo video ready - clearing logo loader");
    setIsLoader(false);
  };

  // Logo video play/pause handlers
  const handleLogoPlay = () => {
    if (logoPlayRef.current) {
      if (logoPlayRef.current.paused) {
        logoPlayRef.current.play().then(() => {
          setIsLogoPaused(false);
          setIsPlaying(true);
        }).catch((error) => {
          console.log("Error playing logo video:", error);
        });
      } else {
        logoPlayRef.current.pause();
        setIsLogoPaused(true);
        setIsPlaying(false);
      }
    }
  };

  // Track logo video events
  useEffect(() => {
    const logoVideo = logoPlayRef.current;
    if (logoVideo && isLogoPlay) {
      const handleLogoPause = () => setIsLogoPaused(true);
      const handleLogoPlayEvent = () => setIsLogoPaused(false);

      logoVideo.addEventListener('pause', handleLogoPause);
      logoVideo.addEventListener('play', handleLogoPlayEvent);

      return () => {
        logoVideo.removeEventListener('pause', handleLogoPause);
        logoVideo.removeEventListener('play', handleLogoPlayEvent);
      };
    }
  }, [isLogoPlay]);

  let mp3Url =
    "https://s3rain.s3.ap-south-1.amazonaws.com/1716374923670_1714108715462_sample-12s.mp3";
  const [musicSrc, setMusicSrc] = useState(null);
  const audioRef = useRef(null);

  const handlePlayMusic = () => {
    setMusicSrc(musicSrc);
  };


  const handleLoad = () => {
    if (!logoAddPlayRef.current) {
      console.warn("Video element not available for playback");
      setIsLoaderAdd(false);
      return;
    }

    var playPromise = logoAddPlayRef.current.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log("Slide/add video is playing after load");
          setIsLoaderAdd(false);
        })
        .catch((error) => {
          console.error("Error playing slide/add video after load:", error);
          setIsLoaderAdd(false);
          // Fallback: try again after a short delay
          setTimeout(() => {
            if (logoAddPlayRef.current) {
              logoAddPlayRef.current.play().catch(() => {
                console.error("Slide/add video playback failed completely, skipping to main video");
                // Skip to main video if add video fails
                handleAddVideoEnd();
              });
            }
          }, 1000);
        });
    } else {
      setIsLoaderAdd(false);
    }

    // Fallback: Clear loader after 3 seconds if still stuck
    setTimeout(() => {
      if (logoAddPlayRef.current && !logoAddPlayRef.current.paused) {
        console.log("Fallback: Clearing stuck loader on slide/add video");
        setIsLoaderAdd(false);
      }
    }, 3000);
  };

  useEffect(() => {
    const videoElement = logoAddPlayRef.current;

    const handlePlaying = () => {
      console.log("Slide/add video playing event - clearing loader");
      setIsLoaderAdd(false);
    };

    if (videoElement) {
      videoElement.addEventListener("canplay", handleLoad);
      videoElement.addEventListener("playing", handlePlaying);

      return () => {
        videoElement.removeEventListener("canplay", handleLoad);
        videoElement.removeEventListener("playing", handlePlaying);
      };
    }
  }, [logoAddPlayRef]);

  const checkQuizAdd = useCallback(() => {
    const fixedCurrentTime = Math.floor(currentTime);
    let filterQuiz = isAddArr?.filter(
      (val) => val.totalTime === fixedCurrentTime
    );

    if (filterQuiz?.length > 0 && !isLogoAddPlay) {
      console.log(`Triggering slide at time ${fixedCurrentTime}:`, filterQuiz[0]);

      // Pause and mute main video immediately
      if (videoPlayerRef?.current) {
        videoPlayerRef.current.pause();
        videoPlayerRef.current.muted = true;
      }

      setIsPlaying(false);
      setIsLoaderEpisode(false);

      // Setup slide/video with music
      if (filterQuiz[0]?.musicId) {
        setMusicSrc(filterQuiz[0]?.musicUrl);
        setIsMusicShow(true);
        setIsAddMuted(true);
      } else {
        setIsAddMuted(false);
      }

      setAddLogoUrl(filterQuiz[0]?.src);
      setIsLoaderAdd(true);
      setIsLogoAddPlay(true);

      // Save timestamp but don't refresh episode data (causes array reset)
      nameFuntion(videoPlayerRef?.current?.currentTime);
    } else if (!filterQuiz?.length && isLogoAddPlay) {
      setIsLogoAddPlay(false);
    }
  }, [currentTime, isLogoAddPlay, isAddArr]);

  useEffect(() => {
    const debouncedCheckQuiz = debounce(checkQuizAdd, 100);
    debouncedCheckQuiz();
    return () => {
      debouncedCheckQuiz?.cancel();
    };
  }, [currentTime, isLogoAddPlay, checkQuizAdd]);

  // Check for placement type 1 (time = 0) slides immediately when array is ready
  useEffect(() => {
    if (isAddArr?.length > 0 && !isLogoAddPlay && currentTime === 0) {
      const initialSlide = isAddArr.find((val) => val.totalTime === 0);
      if (initialSlide) {
        console.log("Found placement type 1 slide - triggering immediately at start:", initialSlide);

        // Pause and mute main video before it even starts
        if (videoPlayerRef?.current) {
          videoPlayerRef.current.pause();
          videoPlayerRef.current.muted = true;
        }

        setIsPlaying(false);
        setIsLoaderEpisode(false);

        // Setup slide/video with music
        if (initialSlide.musicId) {
          setMusicSrc(initialSlide.musicUrl);
          setIsMusicShow(true);
          setIsAddMuted(true);
        } else {
          setIsAddMuted(false);
        }

        setAddLogoUrl(initialSlide.src);
        setIsLoaderAdd(true);
        setIsLogoAddPlay(true);
      }
    }
  }, [isAddArr, isLogoAddPlay, currentTime]);

  const handleAddVideoEnd = () => {
    // Timed video/slide ended, resume main video
    setIsLogoAddPlay(false);
    setIsLoaderAdd(false);
    setIsLoaderEpisode(false);
    setAddLogoUrl("");
    setMusicSrc(null);

    // Stop and cleanup audio
    if (audioRef.current && audioRef.current.audioEl.current) {
      audioRef.current.audioEl.current.pause();
      audioRef.current.audioEl.current.currentTime = 0;
    }
    setIsMusicShow(false);

    if (videoPlayerRef && videoPlayerRef.current) {
      // Unmute and resume main video
      videoPlayerRef.current.muted = isMuted; // Restore original mute state
      videoPlayerRef.current.currentTime = Math.floor(currentTime) + 1;
      setIsPlaying(true);
      setIsLoader(false);
      handlePlay();
    }
  };

  useEffect(() => {
    if (!isLoaderAdd && !isLogoAddPlay) {
      console.log("Auto-playing main video after add content");
      // Ensure main video is unmuted before playing
      if (videoPlayerRef.current) {
        videoPlayerRef.current.muted = isMuted;
      }
      handlePlay();
      setIsMusicShow(false);
    } else if (isLogoAddPlay && videoPlayerRef.current) {
      // Ensure main video stays paused and muted while slide is showing
      if (!videoPlayerRef.current.paused) {
        videoPlayerRef.current.pause();
      }
      if (!videoPlayerRef.current.muted) {
        videoPlayerRef.current.muted = true;
      }
    }
  }, [isLoaderAdd, isLogoAddPlay, isMuted]);

  useEffect(() => {
    if (currentTime > 1) {
      dispatch(setTimeStamp(currentTime));
    }
  }, [currentTime]);

  return (
    <>
      <MS.MediaContainer>
        <MS.MediaInner
          onMouseOver={handleMouseOver}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="media-class-box"
        >
          {/* Network Status Indicator */}
          {networkStatus.isSlowConnection && (
            <MS.NetworkStatusIndicator>
              <p>
                Slow connection detected - Quality adjusted for better playback
              </p>
            </MS.NetworkStatusIndicator>
          )}

          {/* Single Unified Loader - Priority Order */}
          {!isLogoPlay && (isHlsLoading || isLoaderEpisode || isLoader) && (
            <MS.BufferingIndicator>
              <CircleLoader themeColor={theme} />
              <p>Loading...</p>
            </MS.BufferingIndicator>
          )}

          {/* Logo Loader - Only show during logo playback */}
          {isLogoPlay && isLoader && <CircleLoader themeColor={theme} />}
         
          {isLogoPlay ? (
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              <ReactHlsPlayer
                style={{ display: isLoader ? "none" : "flex" }}
                className="video-class-element"
                playerRef={logoPlayRef}
                // src={replaceBaseUrl(logoUrl, "https://cdn.rainmaker.co.in")}
                src={logoUrl}
                muted={false}
                autoPlay={true}
                volume={volume}
                controls={false}
                onEnded={handleVideoEnd}
                onLoadedMetadata={handleVideoReady}
              />

              {/* Center Play/Pause Button for Logo Video */}
              {!isLoader && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    opacity: isLogoPaused ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                    cursor: 'pointer',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    borderRadius: '50%',
                    width: '80px',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    pointerEvents: isLogoPaused ? 'auto' : 'none'
                  }}
                  onClick={handleLogoPlay}
                  onMouseEnter={(e) => e.target.style.opacity = 1}
                  onMouseLeave={(e) => e.target.style.opacity = isLogoPaused ? 1 : 0}
                >
                  <div
                    style={{
                     
                      height: '30px',
                      width:'30px',
                      marginLeft: isLogoPaused ? '4px' : '0'
                    }}
                  >
                    {isLogoPaused ? <img src={playIcon} alt="" style={{width:"100%",height:"100%",objectFit:"contain",border:"none",}} /> :""}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <MS.MediaInnerInner isLogoAddPlay={isLogoAddPlay}>
                {/* Removed duplicate episode loader - handled by unified loader above */}
                {!controlsVisible && (
                  <MS.EpisodeNameRow
                    style={{ display: isLoaderEpisode ? "none" : "flex" }}
                  >
                    <MS.FirstHeaderTwo themeColor={theme}>
                      <p className="title">{courseEpisodeData?.episodeName}</p>
                    </MS.FirstHeaderTwo>
                  </MS.EpisodeNameRow>
                )}
                {bannerThumb?.client_logo?.logoStatus && (
                  <MS.ClientLogo>
                    <img
                      src={bannerThumb?.client_logo?.logo?.logoImage}
                      alt="Logo"
                    />
                  </MS.ClientLogo>
                )}
               
                <ReactHlsPlayer
                  style={{ display: isLoaderEpisode ? "none" : "flex" }}
                  className="video-class-element-media-player"
                  playerRef={videoPlayerRef}
                  // src={replaceBaseUrl(videoSrc, "https://cdn.rainmaker.co.in")}
                  src={videoSrc}
                  muted={isMuted}
                  autoPlay={true}
                  volume={volume}
                  onLoadedMetadata={handleMetadata}
                  onTimeUpdate={handleTimeUpdate}
                  controls={false}
                />
                {true &&
                  (!isLoaderEpisode ? (
                    <Control
                      onPlay={handlePlay}
                      onPause={handlePause}
                      playing={isPlaying}
                      onVolumeChange={volumeChangeHandler}
                      volume={volume * 100}
                      handleMute={toggleMute}
                      isMuted={isMuted}
                      seek={seek}
                      handleNothing={handleSeekAdmin}
                      handleSeekChange={handleSeek}
                      handleSeekForward={handleSeekForward}
                      duration={isDuration}
                      handleSeekVal={setSeek}
                      currentTime={formatTime(currentTime)}
                      qualities={qualities}
                      handleQualityChange={handleQualityChange}
                      currentQuality={currentQuality}
                      toggleFullScreen={toggleFullScreen}
                      controlRef={controlRef}
                      handleProgress={profileData?.switchToUser}
                      isForward={isForward}
                      isBackward={isBackward}
                      videoPlayerRef={videoPlayerRef}
                      maxWatchedTime={maxWatchedTime}
                      controlsVisible={controlsVisible}
                      networkStatus={networkStatus} // Pass network status to controls
                      // isBuffering={isBuffering} // Pass buffering state to controls
                    />
                  ) : (
                    ""
                  ))}
              </MS.MediaInnerInner>

              {isLoaderAdd ? <CircleLoader themeColor={theme} /> : ""}
              <ReactHlsPlayer
                style={{
                  visibility: isLogoAddPlay ? "visible" : "hidden",
                  display: isLoaderAdd ? "none" : "flex",
                }}
                className="add-video-class-element"
                playerRef={logoAddPlayRef}
                // src={replaceBaseUrl(addLogoUrl, "https://cdn.rainmaker.co.in")}
                src={addLogoUrl}
                muted={isAddMuted}
                autoPlay={true}
                controls={false}
                onLoadedMetadata={handleAddMetadata}
                onEnded={handleAddVideoEnd}
              />
              {isMusicShow ? (
                <MS.MusicBox style={{ visibility: "hidden" }}>
                  <ReactAudioPlayer
                    ref={audioRef}
                    src={musicSrc}
                    controls
                    loop={true}
                    onPlay={handlePlayMusic}
                  />
                </MS.MusicBox>
              ) : (
                ""
              )}
            </>
          )}

          {quesOne ? (
            <MS.InnerFirstTwos themeColor={theme}>
              <Formik
                enableReinitialize
                initialValues={filterQuizData}
                validateOnChange
                onSubmit={(values) => handleQuizSubmit(values, false)}
              >
                {(formikBag) => (
                  <Form style={{ width: "100%", position: "relative" }}>
                    <QS.QuestionContainer themeColor={theme}>
                      <QS.QuizTitle themeColor={theme}>
                        <p
                          style={{
                            color: "#e45926",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          Brain Boost: It's Quiz Time{" "}
                          <img
                            style={{
                              height: "20px",
                              width: "20px",
                              border: "none",
                              marginLeft: "5px",
                            }}
                            src={clockTimer}
                            alt=""
                          />
                        </p>
                      </QS.QuizTitle>
                      <FieldArray name="question">
                        {({ form }) => {
                          return (
                            formikBag?.values.question?.length > 0 &&
                            formikBag?.values.question?.map((val, ind) => {
                              return (
                                <QS.QuestionContainer
                                  key={ind}
                                  themeColor={theme}
                                >
                                  <QS.InnerQuestionCon themeColor={theme}>
                                    <QS.QuestionTitle themeColor={theme}>
                                      <p>
                                        {ind + 1}. {val.question}
                                      </p>
                                    </QS.QuestionTitle>
                                    {val?.answerOptions?.map(
                                      (innerVal, answerIndex) => {
                                        return (
                                          <QS.QuestionOption
                                            key={answerIndex}
                                            themeColor={theme}
                                          >
                                            <img
                                              onClick={() => {
                                                if (!answered[ind]) {
                                                  const questionValuesPath = `question.${ind}.answerOptions`;
                                                  const updatedQuestionValues =
                                                    formikBag.values.question[
                                                      ind
                                                    ].answerOptions.map(
                                                      (opt, idx) => ({
                                                        ...opt,
                                                        isAnswers:
                                                          idx === answerIndex,
                                                      })
                                                    );

                                                  formikBag.setFieldValue(
                                                    questionValuesPath,
                                                    updatedQuestionValues
                                                  );
                                                  let newAnswered = [
                                                    ...answered,
                                                  ];
                                                  newAnswered[ind] = true;
                                                  setAnswered(newAnswered);
                                                }
                                              }}
                                              style={{
                                                cursor: answered[ind]
                                                  ? "not-allowed"
                                                  : "pointer",
                                              }}
                                              src={
                                                innerVal.isAnswers
                                                  ? theme
                                                    ? darkCheckRadio
                                                    : checkRadio
                                                  : theme
                                                  ? darkBaseRadio
                                                  : baseRadio
                                              }
                                              alt=""
                                            />
                                            <p>{innerVal.option}</p>
                                          </QS.QuestionOption>
                                        );
                                      }
                                    )}
                                  </QS.InnerQuestionCon>
                                  {getCorrectAnswer(val.answerOptions)}
                                </QS.QuestionContainer>
                              );
                            })
                          );
                        }}
                      </FieldArray>
                      <QS.ButtonGroup themeColor={theme}>
                        {answered[0] ? (
                          <QS.SaveButton
                            cursor={isLoading}
                            disabled={isLoading}
                            className="buttonload"
                            type="submit"
                          >
                            {isLoading ? (
                              <i class="fa fa-spinner fa-spin"></i>
                            ) : (
                              selectLangJson.submit_continue
                            )}
                          </QS.SaveButton>
                        ) : (
                          ""
                        )}
                      </QS.ButtonGroup>
                    </QS.QuestionContainer>
                  </Form>
                )}
              </Formik>
            </MS.InnerFirstTwos>
          ) : (
            ""
          )}
        </MS.MediaInner>
      </MS.MediaContainer>
      <ModalComplete
        isOpen={isPopPo}
        maxWidth="400px"
        theme={theme}
        component={
          <SEC.InnerSectionSuccessSoEpCom
            themeColor={theme}
            style={{ padding: "1rem 0" }}
          >
            <SEC.SuccessIconBoxSo>
              <img src={theme ? successModal : successModal} alt="danger" />
            </SEC.SuccessIconBoxSo>
            <SEC.TitleSo themeColor={theme}>
              {selectLangJson.great}!
            </SEC.TitleSo>
            <SEC.Subtitle themeColor={theme}>{successMsg}</SEC.Subtitle>
            <QS.ButtonGroup style={{ width: "300px" }} themeColor={theme}>
              <QS.CancelButton
                style={{ width: quizId ? "50%" : "100%" }}
                themeColor={theme}
                onClick={() => {
                  restartEpisode();
                }}
              >
                {selectLangJson.restart_episode}
              </QS.CancelButton>
              {quizId ? (
                <QS.SaveButton
                  style={{ width: "50%" }}
                  themeColor={theme}
                  onClick={() => {
                    navigate(`${dynamicRoutes}/learner/quiz`, {
                      state: {
                        _id: quizId,
                      },
                    });
                  }}
                >
                  {selectLangJson.take_quiz}
                </QS.SaveButton>
              ) : (
                ""
              )}
              {nextEpisode ? (
                <QS.SaveButton
                  style={{ width: "50%" }}
                  themeColor={theme}
                  onClick={() => {
                    dispatch(setVideoPlay(true));
                    dispatch(setEpisodeId(nextEpisode));
                    setIsPopPo(false);
                  }}
                >
                  {selectLangJson.next_episode}
                </QS.SaveButton>
              ) : (
                ""
              )}
              {isCourseComplete?.COURSE_COMPLETED &&
              !isCourseComplete?.NEXT_EPISODES ? (
                <QS.SaveButton
                  style={{ width: "50%" }}
                  themeColor={theme}
                  onClick={() => {
                    handleClose();
                    dispatch(setVideoPlay(false));
                    if (isCourseComplete?.Certificate_SCREEN) {
                      navigate(`${dynamicRoutes}/learner/courses/certificate`);
                    } else if (isCourseComplete?.VIEW_POLICY_DOCUMENTS) {
                      navigate(
                        `${dynamicRoutes}/learner/courses/policy-document`
                      );
                    }
                  }}
                >
                  {selectLangJson.complete_course}
                </QS.SaveButton>
              ) : (
                ""
              )}
            </QS.ButtonGroup>
          </SEC.InnerSectionSuccessSoEpCom>
        }
      />
    </>
  );
};

export default MediaPlayer;
